import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { 
  ArrowLeft, MapPin, Clock, Calendar, User, CheckCircle2,
  Navigation, PlayCircle, AlertCircle, FileText
} from "lucide-react";

const statusColors = {
  pending: "bg-slate-100 text-slate-700",
  dispatched: "bg-blue-100 text-blue-700",
  en_route: "bg-amber-100 text-amber-700",
  on_site: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = {
  pending: "Pending",
  dispatched: "Dispatched",
  en_route: "En Route",
  on_site: "On Site",
  completed: "Completed",
  cancelled: "Cancelled",
};

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

export default function WorkOrderDetail() {
  const { workOrderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completionNotes, setCompletionNotes] = useState("");
  const [actualHours, setActualHours] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(`/work-orders/${workOrderId}`);
      setWorkOrder(res.data);
      setCompletionNotes(res.data.completion_notes || "");
      setActualHours(res.data.actual_duration_hours?.toString() || "");
    } catch (error) {
      toast.error("Failed to load work order");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [workOrderId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (newStatus) => {
    try {
      const updateData = { status: newStatus };
      
      // If completing, include notes and duration
      if (newStatus === "completed") {
        if (completionNotes) updateData.completion_notes = completionNotes;
        if (actualHours) updateData.actual_duration_hours = parseFloat(actualHours);
      }
      
      await apiClient.patch(`/work-orders/${workOrderId}`, updateData);
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getNextStatus = (currentStatus) => {
    const flow = {
      pending: "dispatched",
      dispatched: "en_route",
      en_route: "on_site",
      on_site: "completed",
    };
    return flow[currentStatus];
  };

  const getActionLabel = (status) => {
    const labels = {
      pending: "Accept Job",
      dispatched: "Start Route",
      en_route: "Arrive On Site",
      on_site: "Complete Job",
    };
    return labels[status];
  };

  const getActionIcon = (status) => {
    const icons = {
      pending: PlayCircle,
      dispatched: Navigation,
      en_route: MapPin,
      on_site: CheckCircle2,
    };
    return icons[status];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-slate-500">Loading work order...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (!workOrder) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Work order not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const ActionIcon = getActionIcon(workOrder.status);
  const isFieldAgent = user?.role === "field_agent";
  const canUpdateStatus = isFieldAgent && !["completed", "cancelled"].includes(workOrder.status);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="work-order-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{workOrder.title}</h1>
            <p className="text-slate-500 mt-1">Work Order #{workOrder.id.slice(0, 8)}</p>
          </div>
          <Badge className={statusColors[workOrder.status]} variant="secondary">
            {statusLabels[workOrder.status]}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="sag-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 whitespace-pre-wrap">{workOrder.description}</p>
              </CardContent>
            </Card>

            {/* Service Activity Log */}
            <Card className="sag-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Clock className="h-5 w-5 text-sag-navy" />
                  Service Activity Log
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(workOrder.service_logs && workOrder.service_logs.length > 0) ? (
                  <div className="space-y-4">
                    {workOrder.service_logs.map((log, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full mt-1.5 ${idx === workOrder.service_logs.length - 1 ? "bg-sag-navy" : "bg-slate-300"}`} />
                          {idx < workOrder.service_logs.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-1" />}
                        </div>
                        <div className="pb-4">
                          <p className="font-medium text-slate-900 text-sm">{log.note}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {log.by} &middot; {new Date(log.at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No activity logged yet</p>
                )}
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="sag-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-sag-blue" />
                  Location
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 mb-4">{workOrder.location_address}</p>
                {/* Map placeholder */}
                <div className="h-48 bg-slate-100 rounded-xl flex items-center justify-center">
                  <div className="text-center text-slate-400">
                    <MapPin className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">Map view would appear here</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Completion Form (for on_site status) */}
            {workOrder.status === "on_site" && isFieldAgent && (
              <Card className="sag-card border-t-4 border-t-emerald-500">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Complete Work Order
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Completion Notes</Label>
                    <Textarea
                      placeholder="Describe the work completed..."
                      rows={4}
                      value={completionNotes}
                      onChange={(e) => setCompletionNotes(e.target.value)}
                      data-testid="completion-notes-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Actual Duration (hours)</Label>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      placeholder={workOrder.estimated_duration_hours.toString()}
                      value={actualHours}
                      onChange={(e) => setActualHours(e.target.value)}
                      className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-sag-navy focus:ring-2 focus:ring-sag-navy/20"
                      data-testid="actual-hours-input"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Button */}
            {canUpdateStatus && (
              <Button
                onClick={() => updateStatus(getNextStatus(workOrder.status))}
                className="w-full h-14 bg-sag-navy hover:bg-sag-navy-800 text-white font-bold text-lg"
                data-testid="update-status-button"
              >
                {ActionIcon && <ActionIcon className="h-5 w-5 mr-2" />}
                {getActionLabel(workOrder.status)}
              </Button>
            )}
          </div>

          {/* Sidebar - Details */}
          <div className="space-y-6">
            <Card className="sag-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-lg font-heading">Details</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Client */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Client</label>
                  <p className="mt-1 text-slate-900 flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-400" />
                    {workOrder.client_name}
                  </p>
                </div>

                {/* Scheduled Date */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Scheduled Date</label>
                  <p className="mt-1 text-slate-900 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    {new Date(workOrder.scheduled_date).toLocaleDateString()}
                  </p>
                </div>

                {/* Duration */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Estimated Duration</label>
                  <p className="mt-1 text-slate-900 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    {workOrder.estimated_duration_hours} hour(s)
                  </p>
                </div>

                {workOrder.actual_duration_hours && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Actual Duration</label>
                    <p className="mt-1 text-slate-900 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-emerald-500" />
                      {workOrder.actual_duration_hours} hour(s)
                    </p>
                  </div>
                )}

                {/* Priority */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Priority</label>
                  <div className="mt-1">
                    <Badge className={priorityColors[workOrder.priority]}>
                      {workOrder.priority}
                    </Badge>
                  </div>
                </div>

                <Separator />

                {/* Assigned Agent */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Assigned To</label>
                  <p className="mt-1 text-slate-900">
                    {workOrder.assigned_agent_name || "Unassigned"}
                  </p>
                </div>

                {/* Notes */}
                {workOrder.notes && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Notes</label>
                    <p className="mt-1 text-slate-700 text-sm">{workOrder.notes}</p>
                  </div>
                )}

                {/* Completion Notes */}
                {workOrder.completion_notes && (
                  <div>
                    <label className="text-xs font-medium text-slate-500 uppercase">Completion Notes</label>
                    <p className="mt-1 text-slate-700 text-sm">{workOrder.completion_notes}</p>
                  </div>
                )}

                {/* Created */}
                <div>
                  <label className="text-xs font-medium text-slate-500 uppercase">Created</label>
                  <p className="mt-1 text-slate-700 text-sm">
                    {new Date(workOrder.created_at).toLocaleString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
